import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Link } from "react-router-dom";
import { createCheckoutSession, redirectToCheckout } from "../../services/stripeService";
import { getAccountInfo } from "../../services/api";
import toast from "react-hot-toast";

const PlanSelect = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [billingCycle, setBillingCycle] = useState("monthly"); // monthly or annual

  // Check if user was redirected from Stripe checkout cancellation
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const canceled = urlParams.get('canceled');
    
    if (canceled === 'true') {
      toast.error('Payment was canceled. You can try again or select a different plan.');
    }
  }, [location.search]);

  // Plan data matching the image
  const plans = [
    {
      id: "free",
      name: "Free / Viewer",
      price: "$0",
      period: "mo",
      description: "Free viewer access.",
      features: [
        "View Shared Summaries and Transcripts Free"//,
        // "Q&A AI Chatbot access can be added for $5/mo"
      ],
      buttonText: "Create Free Account",
      buttonColor: "bg-blue-600 hover:bg-blue-700",
      isFree: true,
      qaPlan: {
        id: "qa_chatbot",
        name: "Q&A Chatbot Access",
        price: "$6",
        period: "mo",
        description: "Unlimited access to our Q&A Chatbot to get quick answers on what happened in existing sessions and transcripts.",
        features: [
          "Unlimited Q&A AI Chatbot Access",
          "Ask questions about existing sessions and transcripts",
          "Quick answers and insights from your content"
        ],
        buttonText: "Add Q&A Access",
        buttonColor: "bg-green-600 hover:bg-green-700"
      }
    },
    {
      id: "beginner",
      name: "Beginner",
      monthlyPrice: "$12",
      annualPrice: "$9",
      period: "mo",
      description: "Best for groups playing twice per month",
      audioHours: "8 Audio Hours per Month",
      features: [
        "Access to: Transcription, Summarization, Campaign Level Organization",
        "Free Unlimited Q&A AI Chatbot Access"
      ],
      buttonText: "Add Monthly Plan",
      annualButtonText: "Add Annual Plan",
      buttonColor: "bg-blue-600 hover:bg-blue-700",
      annualSavings: "SAVE 25% WITH ANNUAL SUBSCRIPTION",
      annualTotal: "$108/yr"
    },
    {
      id: "standard",
      name: "Standard",
      monthlyPrice: "$22",
      annualPrice: "$16.50",
      period: "mo",
      description: "Best for groups playing every week",
      audioHours: "16 Audio Hours per Month",
      features: [
        "Access to: Transcription, Summarization, Campaign Level Organization",
        "Free Unlimited Q&A AI Chatbot Access"
      ],
      buttonText: "Add Monthly Plan",
      annualButtonText: "Add Annual Plan",
      buttonColor: "bg-blue-600 hover:bg-blue-700",
      annualSavings: "SAVE 25% WITH ANNUAL SUBSCRIPTION",
      annualTotal: "$198/yr"
    },
    {
      id: "pro",
      name: "Pro",
      monthlyPrice: "$45",
      annualPrice: "$34",
      period: "mo",
      description: "Best for groups playing twice per week",
      audioHours: "32 Audio Hours per Month",
      features: [
        "Access to: Transcription, Summarization, Campaign Level Organization",
        "Free Unlimited Q&A AI Chatbot Access"
      ],
      buttonText: "Add Monthly Plan",
      annualButtonText: "Add Annual Plan",
      buttonColor: "bg-blue-600 hover:bg-blue-700",
      annualSavings: "SAVE 25% WITH ANNUAL SUBSCRIPTION",
      annualTotal: "$405/yr"
    }
  ];

  const handlePlanSelect = (planId) => {
    setSelectedPlan(planId);
  };

  const handleContinue = async () => {
    if (!selectedPlan) return;
    
    // Handle Q&A plan case
    if (selectedPlan === "qa_chatbot") {
      const toastId = toast.loading("Setting up payment...");
      try {
        // Get user's email from account info
        let customerEmail = null;
        try {
          const accountInfo = await getAccountInfo();
          customerEmail = accountInfo.email || null;
        } catch (error) {
          console.warn("Could not fetch user email:", error);
          // Continue without email - Stripe will ask for it during checkout
        }
        
        const sessionId = await createCheckoutSession(selectedPlan, billingCycle, customerEmail);
        await redirectToCheckout(sessionId);
        
        // If we get here, the checkout was successful
        navigate("/payment-confirmation", { 
          state: { 
            plan: { id: "qa_chatbot", name: "Q&A Chatbot Access", price: "$6" }, 
            billingCycle: billingCycle,
            isUpgrade: location.state?.isUpgrade || false
          } 
        });
      } catch (error) {
        toast.error("Failed to set up payment. Please try again.");
        console.error("Payment setup error:", error);
      } finally {
        toast.dismiss(toastId);
      }
      return;
    }
    
    const plan = plans.find(p => p.id === selectedPlan);
    if (plan.isFree) {
      // For free plan, redirect to dashboard
      navigate("/dashboard");
    } else {
      // For paid plans, redirect to Stripe checkout
      const toastId = toast.loading("Setting up payment...");
      try {
        // Get user's email from account info
        let customerEmail = null;
        try {
          const accountInfo = await getAccountInfo();
          customerEmail = accountInfo.email || null;
        } catch (error) {
          console.warn("Could not fetch user email:", error);
          // Continue without email - Stripe will ask for it during checkout
        }
        
        const sessionId = await createCheckoutSession(selectedPlan, billingCycle, customerEmail);
        await redirectToCheckout(sessionId);
        
        // If we get here, the checkout was successful
        navigate("/payment-confirmation", { 
          state: { 
            plan: plan, 
            billingCycle: billingCycle,
            isUpgrade: location.state?.isUpgrade || false
          } 
        });
      } catch (error) {
        toast.error("Failed to set up payment. Please try again.");
        console.error("Payment setup error:", error);
      } finally {
        toast.dismiss(toastId);
      }
    }
  };

  return (
    <div className="min-h-screen w-full relative">
      {/* Background Image */}
      <div
        className="absolute inset-0 z-0"
        style={{
          backgroundImage: "url('/imgs/auth-bg.jpg')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}>
      </div>

      {/* Content Container */}
      <div className="relative z-10 min-h-screen flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-6xl">
          {/* Header */}
          <div className="text-center mb-8">
            <img src="/imgs/logo-white.png" alt="TableTop" className="mx-auto mb-4" />
            <h1 className="text-white text-4xl font-bold mb-2">
              {location.state?.isUpgrade ? "Upgrade Your Plan" : "Choose Your Plan"}
            </h1>
            {location.state?.isUpgrade && (
              <p className="text-white text-lg mb-4">
                Select a plan to unlock more features and increase your audio processing limits.
              </p>
            )}
          </div>

          {/* Plans Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {plans.map((plan) => (
              <div
                key={plan.id}
                className={`bg-white rounded-lg border-2 p-6 transition-all duration-200 ${
                  selectedPlan === plan.id
                    ? "border-yellow-400 shadow-lg scale-105"
                    : "border-yellow-400 hover:shadow-md"
                }`}
                onClick={() => handlePlanSelect(plan.id)}
                style={{ cursor: "pointer" }}
              >
                {/* Plan Header */}
                <div className="text-center mb-4">
                  <h3 className="text-xl font-bold text-gray-800 mb-2">
                    {plan.name}
                  </h3>
                  {plan.isFree ? (
                    <div className="text-3xl font-bold text-black mb-2">
                      {plan.price}<span className="text-lg text-gray-600">/{plan.period}</span>
                    </div>
                  ) : (
                    <div className="text-3xl font-bold text-black mb-2">
                      {plan.monthlyPrice}<span className="text-lg text-gray-600">/{plan.period}</span>
                    </div>
                  )}
                  <p className="text-gray-600 text-sm mb-3">
                    {plan.description}
                  </p>
                </div>

                {/* Audio Hours (for paid plans with audio hours) */}
                {!plan.isFree && !plan.isQAPlan && (
                  <div className="text-center mb-4">
                    <div className="text-lg font-semibold text-gray-800">
                      {plan.audioHours}
                    </div>
                  </div>
                )}

                {/* Features */}
                <div className="mb-6">
                  <ul className="space-y-2">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="text-sm text-gray-700">
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Action Buttons */}
                <div className="space-y-2">
                  {plan.isFree ? (
                    <>
                      <button
                        className={`w-full ${plan.buttonColor} text-white py-2 px-4 rounded-lg font-semibold transition-colors`}
                        onClick={(e) => {
                          e.stopPropagation();
                          // For free plan, directly navigate to dashboard
                          navigate("/dashboard");
                        }}
                      >
                        {plan.buttonText}
                      </button>
                      
                      {/* Q&A Plan Section */}
                      {plan.qaPlan && (
                        <div className="mt-4 pt-4 border-t border-gray-200">
                          <div className="text-center mb-3">
                            <h4 className="text-lg font-semibold text-gray-800 mb-1">
                              {plan.qaPlan.name}
                            </h4>
                            <div className="text-2xl font-bold text-green-600 mb-2">
                              {plan.qaPlan.price}<span className="text-sm text-gray-600">/{plan.qaPlan.period}</span>
                            </div>
                            <p className="text-gray-600 text-xs mb-3">
                              {plan.qaPlan.description}
                            </p>
                          </div>
                          
                          {/* Q&A Features */}
                          <div className="mb-4">
                            <ul className="space-y-1">
                              {plan.qaPlan.features.map((feature, index) => (
                                <li key={index} className="text-xs text-gray-700">
                                  {feature}
                                </li>
                              ))}
                            </ul>
                          </div>
                          
                          <button
                            className={`w-full ${plan.qaPlan.buttonColor} text-white py-2 px-4 rounded-lg font-semibold transition-colors`}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedPlan(plan.qaPlan.id);
                              setBillingCycle("monthly");
                              handleContinue();
                            }}
                          >
                            {plan.qaPlan.buttonText}
                          </button>
                        </div>
                      )}
                    </>
                  ) : (
                    // Regular paid plans with monthly and annual options
                    <>
                      <button
                        className={`w-full ${plan.buttonColor} text-white py-2 px-4 rounded-lg font-semibold transition-colors`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedPlan(plan.id);
                          setBillingCycle("monthly");
                          handleContinue();
                        }}
                      >
                        {plan.buttonText}
                      </button>
                      
                      {/* Pricing Details Between Buttons */}
                      <div className="text-center py-2 px-3 bg-gray-50 rounded-lg border">
                        <div className="text-xs text-red-600 font-semibold mb-2">
                          {plan.annualSavings}
                        </div>
                        <div className="text-sm text-gray-600 mb-1">
                          Monthly equivalent: <span className="font-semibold">{plan.annualPrice}/mo</span>
                        </div>
                        <div className="text-sm text-gray-600 mb-1">
                          Annual price: <span className="font-semibold">{plan.annualTotal}</span>
                        </div>
                      </div>
                      
                      <button
                        className={`w-full ${plan.buttonColor} text-white py-2 px-4 rounded-lg font-semibold transition-colors`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedPlan(plan.id);
                          setBillingCycle("annual");
                          handleContinue();
                        }}
                      >
                        {plan.annualButtonText}
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Return to Dashboard Link */}
          <div className="text-center">
            <Link
              to="/dashboard"
              className="inline-block bg-gray-600 hover:bg-gray-500 text-white px-6 py-2 rounded-lg font-semibold transition-colors"
            >
              Return to Dashboard
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlanSelect;
